// Chart of accounts page shell.
// Route: /accounting/chart-of-accounts
// Branches between EmptyState (0 accounts) and Dashboard (populated).
// Row click navigates to the account's history page (register view).

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChartOfAccounts } from "../../../hooks/useChartOfAccounts";
import { useVelaSuggestions } from "../../../hooks/useVelaSuggestions";
import EmptyState from "./components/EmptyState";
import Dashboard from "./components/Dashboard";

export function ChartOfAccountsHeader() {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-3.5 md:px-8">
      <div className="text-[12.5px] text-ink3 mb-1 font-medium">
        <span className="cursor-pointer" onClick={() => navigate("/accounting")}>Accounting</span>
        <span className="text-ink3 mx-1.5">/</span>
        <span>Chart of accounts</span>
      </div>
      <h1 className="text-xl md:text-3xl font-extrabold m-0 -tracking-[0.3px] text-ink">Chart of accounts</h1>
      <div className="text-[12.5px] md:text-[13.5px] text-ink3 mt-1.5 font-medium">
        Vela keeps your chart tidy and tailored to how your business operates.
      </div>
    </div>
  );
}

export default function ChartOfAccountsPage() {
  const navigate = useNavigate();
  const { data: accounts, addAccount, addAccountsFromTemplate, archiveAccounts, mergeAccounts } = useChartOfAccounts();
  const { data: suggestions, dismiss: dismissSuggestion } = useVelaSuggestions();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("healthcare");

  // Row click opens the account's register (history) view.
  const handleRowClick = (id: string) => navigate(`/accounting/chart-of-accounts/${id}/history`);

  const [startBlankOpen, setStartBlankOpen] = useState(false);

  if (accounts.length === 0 && !startBlankOpen) {
    return (
      <div className="max-w-full m-0 -mt-7 px-4 pt-5 pb-24 bg-white md:px-8">
        <EmptyState
          selectedTemplateId={selectedTemplateId}
          onSelectTemplate={setSelectedTemplateId}
          onCreateFromTemplate={addAccountsFromTemplate}
          onStartBlank={() => setStartBlankOpen(true)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-full m-0 -mt-7 px-4 pt-5 pb-24 bg-white md:px-8">
      <Dashboard
        accounts={accounts}
        suggestions={suggestions}
        onCreateAccount={(draft) => { addAccount({ number: draft.number, name: draft.name, type: draft.type }); setStartBlankOpen(false); }}
        onArchiveAccounts={archiveAccounts}
        onMergeAccounts={mergeAccounts}
        onDismissSuggestion={dismissSuggestion}
        onRowClick={handleRowClick}
      />
    </div>
  );
}